import {useContext, useEffect, useRef, useState} from 'react';
import useForm from '../../../hooks/useForm';
import Stack from '../../shared/layouts/Stack';
import Flex from '../../shared/layouts/Flex';
import Header from '../../shared/Header';
import HomeSection from './HomeSection';
import AddressSection from './AddressSection';
import Button from '../../shared/Button';
import axios from 'axios';
import {checkAndFormatDates} from '../../../utils/dateHelpers';
import MessagebarContext from '../../../context/MessagebarContext';
import UserContext from '../../../context/UserContext';
import PreventUnload from '../../shared/PreventUnload';
import {useTheme} from '@emotion/react';
import {useMediaQuery} from '@dnhb/node_package';
import Loader from '../../shared/Loader';
import WhiteLabelContext from '../../../context/WhiteLabelContext';
import ContactAddressBlock from "../profile/ContactAddressBlock";
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";
import ReportIncorrectDataLink from "../../shared/ReportIncorrectDataLink";

const ResidencePage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [addressData, addressDataChange, setAddressData] = useForm({});
  const [isEmptyAddress, setIsEmptyAddress] = useState(false);
  const [house, handleChangeHouse, setHouse] = useForm({});
  const [contactAddressData, setContactAddressData] = useState({});
  const [errors, setErrors] = useState({});
  const mountedRef = useRef(true);
  const { HouseIcon } = useIconLibrary();

  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  const { canEditHouse } = useContext(WhiteLabelContext);
  const { setChangedDataWithoutSaving } = useContext(UserContext);

  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );

  useEffect(() => {
    setChangedDataWithoutSaving(false);
  }, []);

  useEffect(() => {
    Promise.all([axios.get('/dossier/profile'), axios.get('/dossier/address')])
      .then(([{ data: profileData }, { data: addressData }]) => {
        if (!mountedRef.current) return null;
        const { contact_address } = profileData;
        const { address, house } = addressData;


        setContactAddressData(checkAndFormatDates(contact_address) || {});
        setAddressData(checkAndFormatDates(address) || {});
        setIsEmptyAddress(
          !address?.country && !address?.housenumber && !address?.place
          && !address?.province && !address?.street && !address?.zipcode
        );
        setHouse(checkAndFormatDates(house) || {});
      })
      .catch((err) => {
        setAlertMessage();
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);
    axios
      .post('/dossier/address', {
        address: checkAndFormatDates(addressData, true),
        house: checkAndFormatDates(house, true),
      })
      .then(() => {
        setSuccesMessage();
        setErrors({});
        setChangedDataWithoutSaving(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setAlertMessage();
        const errors = err.response?.data?.errors;
        setIsLoading(false);
        if (errors) {
          setErrors(() =>
            Object.keys(errors).reduce(
              (acc, val) => ({
                ...acc,
                [val.replace(/^[^.]*[.]/, '')]: errors[val],
              }),
              {}
            )
          );
        }
      });
  };

  return (
    <PreventUnload>
      {isLoading && <Loader />}
      <Stack
        as="form"
        onSubmit={onSubmit}
        space="xl"
        width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}
      >
        <Header
          title={t("Woning")}
          icon={<HouseIcon height="2rem" width="2rem" />}
          variant="page"
          button={
            addressData &&
            isMinDesktop &&
            canEditHouse && <Button type="submit">{t('Opslaan')}</Button>
          }
        />
        <AddressSection
          {...{ ...addressData, handleChange: addressDataChange, errors, readOnly: !canEditHouse }}
        />
        <HomeSection
          {...{ ...house, handleChange: handleChangeHouse, errors }}
        />
        {(!canEditHouse && isEmptyAddress) && (
        <ContactAddressBlock
          {...{ contactAddressData, addressData, handleChange: function() {}, errors: {} }}
        />
        )}
        {!canEditHouse && (
          <ReportIncorrectDataLink />
        )}
        {canEditHouse && (
          <Flex justifyContent="flex-end">
            <Button type="submit">{t('Opslaan')}</Button>
          </Flex>
        )}
      </Stack>
    </PreventUnload>
  );
};

export default ResidencePage;
